import { gsap } from 'gsap';
import { html } from '../utils';
const BREAKPOINT = 1024;
function getNextSibling(el, selector) {
    let sibling = el.nextElementSibling;
    if (!selector)
        return sibling;
    while (sibling && !sibling.matches(selector)) {
        sibling = sibling.nextElementSibling;
    }
    return sibling;
}
export class NavItem {
    el;
    menu;
    onClick;
    anyMenuOpen;
    constructor(el, onClick) {
        this.el = el;
        this.onClick = onClick;
        this.anyMenuOpen = !!(html && html?.classList.contains('menu-open'));
        const menu = getNextSibling(el, '.mega-menu');
        if (!menu)
            throw new Error('Couldn\'t find mega menu for nav item.');
        this.menu = menu;
        const backButton = menu?.querySelector('.menu-item--back-button');
        if (backButton) {
            backButton.addEventListener('click', () => {
                this.collapse();
            });
        }
        // Bind click
        const handleClick = this.handleClick.bind(this);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.el.addEventListener('click', handleClick);
        const closeButton = this.menu.querySelector('.close-menu');
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        closeButton?.addEventListener('click', handleClick);
        window.addEventListener('resize', this.handleResize.bind(this));
        this.update();
    }
    handleClick(event) {
        event.preventDefault();
        this.onClick(this);
    }
    get expanded() {
        return this.el.getAttribute('aria-expanded') === 'true';
    }
    handleResize() {
        this.update();
    }
    update() {
        const tl = gsap.timeline();
        const megaMenu = this.menu.querySelector('.mega-menu-inner');
        const card = this.menu.querySelector('.card');
        const subMenus = this.menu.querySelectorAll('.menu-item-depth-1');
        const closeButton = this.menu.querySelector('.close-menu');
        gsap.set(megaMenu, { autoAlpha: 1, ease: 'power4' });
        tl.add('start');
        tl.add('end');
        if (this.expanded) {
            // Open
            tl.to(megaMenu, { duration: 0.25, pointerEvents: 'auto', clipPath: 'inset(0% 0% 0% 0%)' }, 'start');
            tl.to(card, { duration: 0.5, clipPath: 'inset(0% 0% 0% 0%)', delay: this.anyMenuOpen ? 0.5 : 0 }, 'end')
                .fromTo([subMenus], {
                autoAlpha: 0,
                y: 24,
                x: 0,
            }, {
                autoAlpha: 1,
                y: 0,
                x: 0,
                stagger: 0.125, duration: 0.25, delay: this.anyMenuOpen ? 0.75 : 0.25
            }, 'end');
            tl.to(closeButton, { duration: 0.5, opacity: 1, delay: this.anyMenuOpen ? 0.75 : 0.25 }, 'end');
        }
        else {
            // Closed
            tl.to(closeButton, { duration: 0.25, opacity: 0 }, 'start')
                .fromTo([subMenus], {
                autoAlpha: 1,
                y: 0,
            }, {
                autoAlpha: 0,
                y: 24,
                x: 0,
                stagger: 0.125, duration: 0.25
            }, 'start')
                .to(card, { duration: 0.5, clipPath: 'inset( 100% 0 0 0)' }, 'start')
                .to(megaMenu, {
                duration: 0.25,
                pointerEvents: 'none',
                clipPath: 'inset( 0 0 100% 0)',
                delay: 0.25
            }, 'start');
        }
    }
    expand() {
        this.el.setAttribute('aria-expanded', 'true');
        this.el.parentElement?.classList.add('is-open');
        this.update();
    }
    collapse() {
        this.el.setAttribute('aria-expanded', 'false');
        this.el.parentElement?.classList.remove('is-open');
        this.update();
    }
}
export class MegaMenu {
    bg;
    navItems;
    windowWidth = window.innerWidth;
    constructor(el) {
        this.bg = document.querySelector('.menu-bg > div');
        // Create nav items
        this.onClickItem = this.onClickItem.bind(this);
        const items = el.querySelectorAll('.menu-item-depth-0.has-sub-menu > a');
        this.navItems = Array.from(items).map((item) => new NavItem(item, this.onClickItem));
        // Handle resize
        window.addEventListener('resize', this.handleResize.bind(this));
        this.handleResize();
    }
    get anyMenuOpen() {
        return this.navItems.some((item) => item.expanded);
    }
    collapseAll() {
        if (this.anyMenuOpen) {
            for (const item of this.navItems)
                item.collapse();
            this.updateGlobalClasses();
        }
    }
    handleResize() {
        if ((window.innerWidth >= BREAKPOINT && this.windowWidth < BREAKPOINT) ||
            (window.innerWidth < BREAKPOINT && this.windowWidth >= BREAKPOINT)) {
            // Collapse all menus on breakpoint change
            this.collapseAll();
        }
        this.windowWidth = window.innerWidth;
        // Update item sizes and global classes
        for (const item of this.navItems)
            item.update();
        this.updateGlobalClasses();
    }
    onClickItem(clickedItem) {
        for (const item of this.navItems) {
            if (item === clickedItem && !item.expanded)
                item.expand();
            else
                item.collapse();
        }
        this.updateGlobalClasses();
    }
    updateGlobalClasses() {
        if (this.anyMenuOpen) {
            html?.classList.add('menu-open');
        }
        else {
            html?.classList.remove('menu-open');
        }
    }
}
